import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { useContacts } from '@/hooks/useDispatcher';
import { formatDateTime } from '@/lib/utils';
import type { Contact } from '@/types/api';

export default function ContactsPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { data, isLoading } = useContacts(query);

  const columns = useMemo<ColumnDef<Contact>[]>(
    () => [
      { accessorKey: 'company_name', header: 'Entreprise', cell: ({ getValue }) => getValue<string>() ?? '—' },
      { accessorKey: 'email', header: 'Email', cell: ({ getValue }) => getValue<string>() ?? '—' },
      { accessorKey: 'phone', header: 'Téléphone', cell: ({ getValue }) => getValue<string>() ?? '—' },
      {
        accessorFn: (c) => `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || '—',
        id: 'name',
        header: 'Contact',
      },
      { accessorKey: 'industry', header: 'Industrie', cell: ({ getValue }) => getValue<string>() ?? '—' },
      { accessorKey: 'domain', header: 'Domaine', cell: ({ getValue }) => getValue<string>() ?? '—' },
      {
        accessorKey: 'created_at',
        header: 'Créé',
        cell: ({ getValue }) => formatDateTime(getValue<string>()),
      },
    ],
    [],
  );

  return (
    <div>
      <PageHeader title="Contacts" description="Explorateur du dataset enrichi" />

      <div className="mb-4 max-w-md">
        <Input
          type="search"
          placeholder="Rechercher email, entreprise, domaine, téléphone…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <DataTable
        data={data}
        columns={columns}
        loading={isLoading}
        searchPlaceholder="Filtrer la liste affichée…"
        onRowClick={(row) => navigate(`/contacts/${row.id}`)}
        emptyTitle={query ? 'Aucun résultat' : 'Tapez une recherche pour explorer'}
      />
    </div>
  );
}
